/**
 * ARIA Enhancements Component
 * 
 * Provides comprehensive ARIA support and accessibility enhancements
 * for improved screen reader compatibility and keyboard navigation.
 */

import React, { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useI18n } from '../../hooks/useI18n'

interface ARIAEnhancementsProps {
  /**
   * Enable live region announcements
   */
  enableLiveRegions?: boolean
  
  /**
   * Enable automatic landmark detection and enhancement
   */
  enableLandmarkEnhancement?: boolean
  
  /**
   * Enable focus management
   */
  enableFocusManagement?: boolean
  
  /**
   * Enable keyboard navigation enhancements
   */
  enableKeyboardNavigation?: boolean
}

export const ARIAEnhancements: React.FC<ARIAEnhancementsProps> = ({
  enableLiveRegions = true,
  enableLandmarkEnhancement = true,
  enableFocusManagement = true,
  enableKeyboardNavigation = true
}) => {
  const location = useLocation()
  const { currentLanguage, isLoading } = useI18n()
  const liveRegionRef = useRef<HTMLDivElement>(null)
  const statusRef = useRef<HTMLDivElement>(null)

  // Announce page changes to screen readers
  useEffect(() => {
    if (enableLiveRegions && liveRegionRef.current) {
      const pageName = document.title || 'Page'
      const announcement = `Navigated to ${pageName}`
      
      setTimeout(() => {
        if (liveRegionRef.current) {
          liveRegionRef.current.textContent = announcement
        }
      }, 100)
    }
  }, [location.pathname, enableLiveRegions])

  // Announce loading states
  useEffect(() => {
    if (enableLiveRegions && statusRef.current) {
      const status = isLoading ? 'Loading...' : 'Content loaded'
      statusRef.current.textContent = status
    }
  }, [isLoading, enableLiveRegions])

  // Enhance landmarks on mount
  useEffect(() => {
    if (enableLandmarkEnhancement) {
      enhanceLandmarks()
    }
  }, [enableLandmarkEnhancement])

  // Focus management
  useEffect(() => {
    if (enableFocusManagement) {
      setupFocusManagement()
    }
  }, [location.pathname, enableFocusManagement])

  // Keyboard navigation
  useEffect(() => {
    if (enableKeyboardNavigation) {
      const cleanup = setupKeyboardNavigation()
      return cleanup
    }
  }, [enableKeyboardNavigation])

  // Language announcements
  useEffect(() => {
    if (enableLiveRegions && liveRegionRef.current) {
      const languageNames = {
        en: 'English',
        es: 'Spanish',
        fr: 'French'
      }
      const langName = languageNames[currentLanguage as keyof typeof languageNames] || currentLanguage
      
      setTimeout(() => {
        if (liveRegionRef.current) {
          liveRegionRef.current.textContent = `Language changed to ${langName}`
        }
      }, 100)
    }
  }, [currentLanguage, enableLiveRegions])

  return (
    <>
      {/* Live Regions for Screen Reader Announcements */}
      {enableLiveRegions && (
        <>
          {/* Polite announcements for navigation and content changes */}
          <div
            ref={liveRegionRef}
            aria-live="polite"
            aria-atomic="true"
            className="sr-only"
            id="aria-live-region"
          />
          
          {/* Assertive announcements for urgent status updates */}
          <div
            ref={statusRef}
            aria-live="assertive"
            aria-atomic="true"
            className="sr-only"
            id="aria-status-region"
          />
          
          {/* Alert region for errors and important notifications */}
          <div
            role="alert"
            aria-atomic="true"
            className="sr-only"
            id="aria-alert-region"
          />
        </>
      )}
    </>
  )
}

/**
 * Enhance existing landmarks with proper ARIA attributes
 */
function enhanceLandmarks() {
  // Main content area
  const main = document.querySelector('main')
  if (main) {
    if (!main.getAttribute('role')) {
      main.setAttribute('role', 'main')
    }
    if (!main.getAttribute('aria-label') && !main.querySelector('h1')) {
      main.setAttribute('aria-label', 'Main content')
    }
  }

  // Navigation areas
  const navs = document.querySelectorAll('nav')
  navs.forEach((nav, index) => {
    if (!nav.getAttribute('aria-label') && !nav.getAttribute('aria-labelledby')) {
      const label = nav.id === 'main-navigation' ? 'Main navigation' : `Navigation ${index + 1}`
      nav.setAttribute('aria-label', label)
    }
  })

  // Header
  const header = document.querySelector('header')
  if (header && !header.getAttribute('role')) {
    header.setAttribute('role', 'banner')
  }

  // Footer
  const footer = document.querySelector('footer')
  if (footer && !footer.getAttribute('role')) {
    footer.setAttribute('role', 'contentinfo')
  }

  // Aside elements
  const asides = document.querySelectorAll('aside')
  asides.forEach((aside, index) => {
    if (!aside.getAttribute('role')) {
      aside.setAttribute('role', 'complementary')
    }
    if (!aside.getAttribute('aria-label') && !aside.getAttribute('aria-labelledby')) {
      aside.setAttribute('aria-label', `Sidebar ${index + 1}`)
    }
  })

  // Form landmarks
  const forms = document.querySelectorAll('form')
  forms.forEach((form) => {
    if (!form.getAttribute('aria-label') && !form.getAttribute('aria-labelledby')) {
      const heading = form.querySelector('h1, h2, h3, h4, h5, h6')
      if (heading) {
        const headingId = heading.id || `form-heading-${Math.random().toString(36).substr(2, 9)}`
        heading.id = headingId
        form.setAttribute('aria-labelledby', headingId)
      } else {
        form.setAttribute('aria-label', 'Form')
      }
    }
  })

  // Search landmarks
  const searchForms = document.querySelectorAll('form[role="search"], .search-form, [data-search-form]')
  searchForms.forEach((form) => {
    form.setAttribute('role', 'search')
    if (!form.getAttribute('aria-label')) {
      form.setAttribute('aria-label', 'Search')
    }
  })

  // Enhance lists with proper ARIA
  const lists = document.querySelectorAll('ul[role="menu"], ol[role="menu"], ul[role="menubar"], ol[role="menubar"]')
  lists.forEach((list) => {
    const items = list.querySelectorAll('li')
    items.forEach((item) => {
      if (!item.getAttribute('role')) {
        item.setAttribute('role', 'menuitem')
      }
    })
  })
}

/**
 * Set up focus management for route changes
 */
function setupFocusManagement() {
  // Find the main heading or main content area
  const mainHeading = document.querySelector('main h1, h1, [role="main"] h1')
  const mainContent = document.querySelector('main, [role="main"]')
  
  if (mainHeading) {
    // Focus the main heading
    const headingElement = mainHeading as HTMLElement
    if (!headingElement.getAttribute('tabindex')) {
      headingElement.setAttribute('tabindex', '-1')
    }
    headingElement.focus()
  } else if (mainContent) {
    // Focus the main content area
    const contentElement = mainContent as HTMLElement
    if (!contentElement.getAttribute('tabindex')) {
      contentElement.setAttribute('tabindex', '-1')
    }
    contentElement.focus()
  }
}

/**
 * Set up keyboard navigation enhancements
 */
function setupKeyboardNavigation() {
  const handleKeyDown = (event: KeyboardEvent) => {
    // Alt + S: Skip to search
    if (event.altKey && event.key === 's') {
      event.preventDefault()
      const search = document.querySelector('input[type="search"], [role="search"] input, #search-input')
      if (search) {
        (search as HTMLElement).focus()
      }
    }

    // Alt + M: Skip to main content
    if (event.altKey && event.key === 'm') {
      event.preventDefault()
      const main = document.querySelector('main, [role="main"]')
      if (main) {
        const mainElement = main as HTMLElement
        if (!mainElement.getAttribute('tabindex')) {
          mainElement.setAttribute('tabindex', '-1')
        }
        mainElement.focus()
      }
    }

    // Alt + N: Skip to navigation
    if (event.altKey && event.key === 'n') {
      event.preventDefault()
      const nav = document.querySelector('nav, [role="navigation"]')
      if (nav) {
        const navElement = nav as HTMLElement
        if (!navElement.getAttribute('tabindex')) {
          navElement.setAttribute('tabindex', '-1')
        }
        navElement.focus()
      }
    }

    // Escape: Clear focus from modals/dropdowns
    if (event.key === 'Escape') {
      const activeElement = document.activeElement as HTMLElement
      if (activeElement && (
        activeElement.closest('[role="dialog"]') ||
        activeElement.closest('.dropdown-menu') ||
        activeElement.closest('.modal')
      )) {
        activeElement.blur()
      }
    }
  }

  document.addEventListener('keydown', handleKeyDown)

  // Cleanup function
  return () => {
    document.removeEventListener('keydown', handleKeyDown)
  }
}

/**
 * Hook for ARIA live region announcements
 */
export const useARIALiveRegion = () => {
  const announce = (message: string, priority: 'polite' | 'assertive' | 'alert' = 'polite') => {
    const regionId = priority === 'alert' ? 'aria-alert-region' : 
                    priority === 'assertive' ? 'aria-status-region' : 'aria-live-region'
    
    const region = document.getElementById(regionId)
    if (region) {
      // Clear previous message
      region.textContent = ''
      
      // Announce new message after a brief delay
      setTimeout(() => {
        region.textContent = message
      }, 100)
      
      // Clear message after announcement
      setTimeout(() => {
        region.textContent = ''
      }, 3000)
    }
  }

  const announceError = (message: string) => {
    announce(message, 'alert')
  }

  const announceStatus = (message: string) => {
    announce(message, 'assertive')
  }

  const announceInfo = (message: string) => {
    announce(message, 'polite')
  }

  return {
    announce,
    announceError,
    announceStatus,
    announceInfo
  }
}

/**
 * Hook for managing focus
 */
export const useFocusManagement = () => {
  const focusElement = (selector: string) => {
    const element = document.querySelector(selector) as HTMLElement
    if (element) {
      if (!element.getAttribute('tabindex') && !element.matches('a, button, input, textarea, select')) {
        element.setAttribute('tabindex', '-1')
      }
      element.focus()
    }
  }

  const focusById = (id: string) => {
    const element = document.getElementById(id) as HTMLElement
    if (element) {
      if (!element.getAttribute('tabindex') && !element.matches('a, button, input, textarea, select')) {
        element.setAttribute('tabindex', '-1')
      }
      element.focus()
    }
  }

  const manageFocusForRouteChange = () => {
    setupFocusManagement()
  }

  return {
    focusElement,
    focusById,
    manageFocusForRouteChange
  }
}

/**
 * Component for ARIA labels and descriptions
 */
interface ARIALabelProps {
  id: string
  children: React.ReactNode
  level?: 'label' | 'description'
}

export const ARIALabel: React.FC<ARIALabelProps> = ({ id, children, level = 'label' }) => {
  return (
    <span 
      id={id}
      className={level === 'description' ? 'sr-only text-sm text-gray-600' : 'sr-only'}
    >
      {children}
    </span>
  )
}

export default ARIAEnhancements